import { FillOrderData } from "shared/cubiq-services";
import { Subject } from "rxjs";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { TriangulationUtil } from "./TriangulationUtil";

const LEG1: string = "-leg1";
const LEG2: string = "-leg2";

export class FillOrderGroup {
  public removedSubject: Subject<any> = new Subject();
  public addedSubject: Subject<any> = new Subject();
  public dataChangedSubject: Subject<any> = new Subject();
  public pendingChangeSubject: Subject<{
    deselectIds: string[];
    // TODO confirm if needed
    selectIds: string[];
  }> = new Subject();
  public ids: Array<string> = [];
  public refs: Array<string> = []; // parallel to ids
  public queuedIds: Set<string> = new Set();
  public selectedIds: Array<string> = [];
  public selectedTpIds: Array<string> = [];
  public selectedMKTIds: Array<string> = [];
  public selectedSlIds: Array<string> = [];
  public selectedTriggeredIds: Array<string> = [];
  public slippage: any;
  public posBook: any;
  public vwap: string;
  public vwapIntent: CubiqTS.OrderIntent;

  public vwapUpdateSubject: Subject<void> = new Subject();

  private rowMap: Map<string, any> = new Map();

  constructor(
    public pairName: string,
    public rowData: Array<any>,
    private minPrecision: number,
    private maxPrecision: number,
    private autoSlipage: CubiqTS.BigNumber,
  ) {
    rowData.forEach((element) => {
      element.vwapUpdateSubject = this.vwapUpdateSubject;
      this.rowMap.set(element.key, element);
      this.ids.push(element.key);
      this.refs.push(element.orderRef);
    });
    rowData.sort(this.sortFillOrders.bind(this));

    if (this.autoSlipage != null) {
      this.setSlippage(this.autoSlipage.toFixed());
    }

    this.vwapUpdateSubject.subscribe(this.doVwap.bind(this));
  }

  selectionChanged(orders: any): void {
    this.selectedIds = [];
    this.selectedTpIds = [];
    this.selectedSlIds = [];
    this.selectedTriggeredIds = [];
    this.selectedMKTIds = [];
    if (orders) {
      orders.forEach((order) => {
        this.selectedIds.push(order.orderId);
        if (CubiqTS.OrderState.TRIGGERED === order.orderState) {
          this.selectedTriggeredIds.push(order.orderId);
        }
        if (CubiqTS.RequestOrderType.TP === order.requestedOrderType) {
          this.selectedTpIds.push(order.orderId);
        }
        if (CubiqTS.RequestOrderType.MARKET === order.requestedOrderType) {
          this.selectedMKTIds.push(order.orderId);
        }
        if (this.isSl(order)) {
          this.selectedSlIds.push(order.orderId);
        }
      });
    }

    this.doVwap();
  }

  private isSl(order): boolean {
    switch (order.requestedOrderType) {
      case CubiqTS.RequestOrderType.SL_ATRATE:
      case CubiqTS.RequestOrderType.SL_BID:
      case CubiqTS.RequestOrderType.SL_ASK:
      case CubiqTS.RequestOrderType.TSL:
      case CubiqTS.RequestOrderType.TSL_BID:
      case CubiqTS.RequestOrderType.TSL_ASK:
        return true;
      default:
        return false;
    }
  }

  getFillIdsToUnlock(): Array<string> {
    let allNonQueued: Array<string> = [];

    this.ids.forEach((id) => {
      if (!this.queuedIds.has(id)) {
        allNonQueued.push(id);
      }
    });

    allNonQueued.forEach((id) => {
      this.deleteRowById(id, false);
    });
    return allNonQueued;
  }

  getSelectedTriggerIdsToReactivate(addToQueue: boolean = false): Array<string> {
    if (!addToQueue) return this.selectedTriggeredIds;
    let allNonQueued: Array<string> = [];
    this.selectedTriggeredIds.forEach((id) => {
      if (!this.queuedIds.has(id)) {
        allNonQueued.push(id);
      }
    });

    allNonQueued.forEach((id) => {
      this.deleteRowById(id);
    });
    // if(addToQueue){
    //     this.pendingChangeSubject.next();
    // }
    return allNonQueued;
  }

  getSelectedFills(addToQueue: boolean = false): Array<FillOrderData> {
    let selectedData: Array<FillOrderData> = [];
    let hasAddedToQueue = false;

    this.selectedIds.forEach((id) => {
      if (addToQueue) {
        if (!this.queuedIds.has(id)) {
          hasAddedToQueue = true;
          selectedData.push(this.orderToData(this.rowMap.get(id)));
          this.queuedIds.add(id);
        }
      } else {
        //
        selectedData.push(this.orderToData(this.rowMap.get(id)));
      }
    });
    if (hasAddedToQueue) {
      this.pendingChangeSubject.next({
        deselectIds: [...this.queuedIds],
        selectIds: [],
      });
    }
    return selectedData;
  }
  getSelectedTPFills(addToQueue: boolean = false): Array<FillOrderData> {
    let selectedData: Array<FillOrderData> = [];
    let hasAddedToQueue = false;

    this.selectedTpIds.forEach((id) => {
      if (addToQueue) {
        if (!this.queuedIds.has(id)) {
          hasAddedToQueue = true;
          selectedData.push(this.orderToData(this.rowMap.get(id)));
          this.queuedIds.add(id);
        }
      } else {
        selectedData.push(this.orderToData(this.rowMap.get(id)));
      }
    });

    if (hasAddedToQueue) {
      this.pendingChangeSubject.next({
        deselectIds: [...this.queuedIds],
        selectIds: [...this.selectedMKTIds, ...this.selectedSlIds],
      });
    }
    return selectedData;
  }

  getSelectedSLFills(addToQueue: boolean = false): Array<FillOrderData> {
    let selectedData: Array<FillOrderData> = [];
    let hasAddedToQueue = false;
    this.selectedSlIds.forEach((id) => {
      if (addToQueue) {
        if (!this.queuedIds.has(id)) {
          hasAddedToQueue = true;
          selectedData.push(this.orderToData(this.rowMap.get(id)));
          this.queuedIds.add(id);
        }
      } else {
        selectedData.push(this.orderToData(this.rowMap.get(id)));
      }
    });

    if (hasAddedToQueue) {
      this.pendingChangeSubject.next({
        deselectIds: [...this.queuedIds],
        selectIds: [...this.selectedTpIds, ...this.selectedMKTIds],
      });
    }

    return selectedData;
  }

  getSelectedMKTFills(addToQueue: boolean = false): Array<FillOrderData> {
    let selectedData: Array<FillOrderData> = [];
    let hasAddedToQueue = false;
    this.selectedMKTIds.forEach((id) => {
      if (addToQueue) {
        if (!this.queuedIds.has(id)) {
          hasAddedToQueue = true;
          selectedData.push(this.orderToData(this.rowMap.get(id)));
          this.queuedIds.add(id);
        }
      } else {
        selectedData.push(this.orderToData(this.rowMap.get(id)));
      }
    });
    if (addToQueue) {
      this.pendingChangeSubject.next({
        deselectIds: [...this.queuedIds],
        selectIds: [...this.selectedTpIds, ...this.selectedSlIds],
      });
    }
    return selectedData;
  }

  public addRow(row: any): void {
    if (!this.rowMap.get(row.key)) {
      this.rowData.push(row);
      this.rowData.sort(this.sortFillOrders.bind(this));
    } else {
      let idx: number = this.rowData.findIndex((drow) => {
        return drow.key == row.key;
      });
      if (idx > -1) {
        this.rowData.splice(idx, 1, row);
      }
    }

    row.vwapUpdateSubject = this.vwapUpdateSubject;

    this.rowMap.set(row.key, row);
    if (!row.isComponentLeg) {
      if (this.ids.indexOf(row.key) == -1) {
        this.ids.push(row.key);
        this.refs.push(row.orderRef);
      }
    }
    this.addedSubject.next(row);

    if (this.slippage != null && this.slippage != "") {
      this.setSlippage(this.slippage);
    }
  }

  // returns true if we didnt have a pending action, returns false if we did
  // this is so we know it was unlocked outside of the detail fill window
  // it also has to exist in the model for it to retur true
  public deleteRowById(id: string, notify: boolean = true): boolean {
    let wasNotExpected: boolean = false;
    let idIndex = this.ids.indexOf(id);

    if (this.ids.indexOf(id) > -1) {
      let idx: number = this.rowData.findIndex((drow) => {
        return drow.key == id;
      });
      if (idx > -1) {
        this.rowData.splice(idx, 1);
        if (!this.queuedIds.has(id)) {
          wasNotExpected = true;
        }
      }
      let rowToDelete = this.rowMap.get(id);
      rowToDelete["leg1"] = null;
      rowToDelete["leg2"] = null;
      this.refs.splice(idIndex, 1);
      this.rowMap.delete(id);
      this.queuedIds.delete(id);
      this.removeIdFromArr(id, this.ids);
      this.removeIdFromArr(id, this.selectedIds);
      this.removeIdFromArr(id, this.selectedSlIds);
      this.removeIdFromArr(id, this.selectedTpIds);
      this.removeIdFromArr(id, this.selectedTriggeredIds);
      this.removeIdFromArr(id, this.selectedMKTIds);
      if (notify) {
        this.removedSubject.next(rowToDelete);
      }
      if (id.indexOf("-") == -1) {
        this.deleteCrossLegsByOrderId(id);
      }
    }
    return wasNotExpected;
  }

  public removeFromQueue(id: string): boolean {
    if (this.queuedIds.has(id)) {
      this.queuedIds.delete(id);
      // TODO - check might not be requested anymore...
      this.pendingChangeSubject.next({ deselectIds: [], selectIds: [id] });
      return true;
    }
    return false;
  }

  // returns true if we didnt have a pending action, returns false if we did
  // this is so we know it was unlocked outside of the detail fill window
  // it also has to exist in the model for it to retur true
  public deleteCrossLegsByOrderId(id: string): void {
    this.deleteCrossLeg(id + LEG2);
    this.deleteCrossLeg(id + LEG1);
  }
  private deleteCrossLeg(key: string): void {
    let idx: number = this.rowData.findIndex((drow) => {
      return drow.key == key;
    });
    if (idx > -1) {
      this.rowData.splice(idx, 1);
    }
    let rowToDelete = this.rowMap.get(key);
    if (rowToDelete) {
      rowToDelete["posBooks"] = null;
      rowToDelete["leg1"] = null;
      rowToDelete["leg2"] = null;
      rowToDelete["parentOrder"] = null;
      this.rowMap.delete(key);
      this.removedSubject.next(rowToDelete);
    }
  }

  private removeIdFromArr(id: string, arr: Array<string>): boolean {
    let idx: number = arr.indexOf(id);
    if (idx > -1) {
      arr.splice(idx, 1);
      return true;
    }
    return false;
  }

  private orderToData(order: any): FillOrderData {
    let fod: FillOrderData = new FillOrderData();

    fod.orderId = order.orderId;
    fod.orderRef = order.orderRef;

    let pairArr: Array<string> = order.instrument.split("/");
    fod.baseCcy = pairArr[0];
    fod.nonBaseCcy = pairArr[1];

    fod.clientSpot = order.clientSpot.toString();
    fod.clientPoints = order.forwardPoints.toString();
    fod.clientAllIn = order.allInClientRate.toString();

    fod.mktSpot = order.spotMarketRate.toString();
    fod.mktPoints = order.forwardMarketPoints.toString();
    fod.mktAllIn = order.forwardMarketRate.toString();

    fod.dealtCcy = order.dealtCcy;
    if (order.fillAmount != null) {
      fod.fillAmt = order.fillAmount.toString();
    }

    fod.isInvert = order.invertedOrder;
    fod.stp = order.stp;
    fod.stpHold = order.stpHold;

    if (order.leg1 && order.leg2) {
      let leg1: FillOrderData = this.legToData(order.leg1);
      let leg2: FillOrderData = this.legToData(order.leg2);
      fod.crosses = [leg1, leg2];
    } else {
      fod.positionBookId = order.positionBookId;
    }

    if (order?.valueDateRequested) {
      fod.valueDate = order.valueDate;
      fod.lockedValueDate = order.lockedValueDate;
    }

    fod.settleTenor = order.settleTenor;
    fod.adjustedSettleTenor = order.adjustedSettleTenor;
    fod.product = order.product;

    return fod;
  }

  private legToData(leg: any): FillOrderData {
    let legData: FillOrderData = new FillOrderData();
    legData.baseCcy = leg.baseCcyCd;
    legData.nonBaseCcy = leg.nonBaseCcyCd;
    legData.mktSpot = leg.spotMarketRate;
    legData.clientSpot = leg.clientSpot;
    legData.positionBookId = leg.as_fv_positionBookName;
    return legData;
  }

  public doVwap(): void {
    let totalCounterPosition: CubiqTS.BigNumber = new CubiqTS.BigNumber(0);

    // For Position so can be +/-
    let totalBasePosition: CubiqTS.BigNumber = new CubiqTS.BigNumber(0);

    this.selectedIds.forEach((id) => {
      let order = this.rowMap.get(id);
      if (order) {
        if (order.orderType != CubiqTS.OrderType.LIMIT_CALL) {
          let orderBaseAmount: CubiqTS.BigNumber;

          let marketRate: CubiqTS.BigNumber = order.spotMarketRate;

          orderBaseAmount = this.fixedIsBase(order)
            ? new CubiqTS.BigNumber(order.fillAmount)
            : order.invertedOrder
            ? new CubiqTS.BigNumber(order.fillAmount).times(marketRate)
            : new CubiqTS.BigNumber(new CubiqTS.BigNumber(order.fillAmount).div(marketRate));

          // Order of signs here doesn't matter so
          // use bank POV
          if (order.clientBuysBase == order.invertedOrder) {
            orderBaseAmount = orderBaseAmount.times(-1);
          }

          totalBasePosition = totalBasePosition.plus(orderBaseAmount);
          totalCounterPosition = totalCounterPosition.plus(
            order.invertedOrder
              ? new CubiqTS.BigNumber(orderBaseAmount.div(marketRate))
              : orderBaseAmount.times(marketRate),
          );
        }
      }
    });
    if (totalBasePosition.isEqualTo(0)) {
      this.vwap = "Buy 0.00m @ 0.00000";
      this.vwapIntent = CubiqTS.OrderIntent.BUY;
    } else {
      let intent: string;
      if (totalBasePosition.isGreaterThanOrEqualTo(0)) {
        intent = "Buy";
        this.vwapIntent = CubiqTS.OrderIntent.BUY;
      } else {
        intent = "Sell";
        this.vwapIntent = CubiqTS.OrderIntent.SELL;
      }
      let amt: string = totalBasePosition.dividedBy(1000000).abs().toFixed(2); // NB : Should be to CCY precision?
      let vwapRt = totalCounterPosition.dividedBy(totalBasePosition);

      let vwapRtStr: string;
      let currentDecs: number = vwapRt.decimalPlaces();
      if (currentDecs < this.minPrecision) {
        vwapRtStr = vwapRt.toFixed(this.minPrecision);
      } else if (currentDecs > this.maxPrecision) {
        vwapRtStr = vwapRt.toFixed(this.maxPrecision);
      } else {
        vwapRtStr = vwapRt.toString();
      }
      this.vwap = intent + " " + amt + "m @ " + this.addFormatting(vwapRtStr);
    }
  }

  private addFormatting(rate: string): string {
    let decIdx = rate.indexOf(".");
    let startTag = "<span class='bfx-vwap-primary'>";
    let endTag = "</span>";
    if (decIdx > -1) {
      let startRate: string = rate.substring(0, decIdx + 1 + this.minPrecision);
      let endRate: string = rate.substring(decIdx + 1 + this.minPrecision, rate.length);
      return startTag + startRate + endTag + endRate;
    }
    return startTag + rate + endTag;
  }

  private amBuyingBase(order: any): boolean {
    let base = this.pairName.substr(0, 3);
    let dealt = order.dealtCcy;
    if (base != dealt) {
      return order.clientIntent === CubiqTS.OrderIntent.SELL;
    }
    return order.clientIntent === CubiqTS.OrderIntent.BUY;
  }

  private fixedIsBase(order: any): boolean {
    let base = this.pairName.substr(0, 3);
    let dealt = order.dealtCcy;
    if (base != dealt) {
      return false;
    }
    return true;
  }

  private sortFillOrders(orderA, orderB) {
    // if its a compoinent - we will use its parents detail - so sorts thesame - except on orderId - where it will sort lower than its parent.
    let testOrderA = orderA;
    let testOrderIdA = Number(orderA.orderId);
    if (orderA.isComponentLeg) {
      testOrderA = orderA.parentOrder;
      testOrderIdA = Number(orderA.parentOrder.orderId + "." + orderA.legNum);
    }

    let testOrderB = orderB;
    let testOrderIdB = Number(orderB.orderId);
    if (orderB.isComponentLeg) {
      testOrderB = orderB.parentOrder;
      testOrderIdB = Number(orderB.parentOrder.orderId + "." + orderB.legNum);
    }

    // Else sort by reverse spot rate
    let priceDiff: number = Number(testOrderA.spotMarketRate) - Number(testOrderB.spotMarketRate);
    if (priceDiff > 0) return -1;
    if (priceDiff < 0) return 1;

    if (this.isSl(testOrderA) != this.isSl(testOrderB)) {
      return this.isSl(testOrderA) ? -1 : 1;
    }

    // Both orders are of same type - if intents are different order bank Buys first
    if (this.amBuyingBase(testOrderA) != this.amBuyingBase(testOrderB)) {
      return this.amBuyingBase(testOrderA) ? -1 : 1;
    }

    // Otherwise by amount with largest fixed amount first - fixed amount never changes
    // so never any need to dispatch property change event to refresh sort order
    let amtDiff: number =
      (testOrderA.fixedAmount ? Number(testOrderA.fixedAmount.toString()) : 0) -
      (testOrderB.fixedAmount ? Number(testOrderB.fixedAmount.toString()) : 0);
    if (amtDiff > 0) return -1;
    if (amtDiff < 0) return 1;

    if (testOrderIdA < testOrderIdB) return -1;
    if (testOrderIdA > testOrderIdB) return 1;

    if (orderA.key < orderB.key) return -1;
    if (orderA.key > orderB.key) return 1;
    return 0;
  }

  private getFixed(order): string {
    return order.dealtCcy;
  }
  private getBaseCcy(order): string {
    if (order.invertedOrder) {
      return order.instrument.split("/")[1];
    }
    return order.instrument.split("/")[0];
  }
  private getNoBaseCcy(order): string {
    if (order.invertedOrder) {
      return order.instrument.split("/")[0];
    }
    return order.instrument.split("/")[1];
  }

  getBase(): string {
    return this.pairName.substr(0, 3);
  }
  getNonBase(): string {
    return this.pairName.substr(3, 3);
  }

  setPositionBook(data): void {
    this.posBook = data;
    let updatedRows: Array<any> = [];
    this.rowData.forEach((element) => {
      if (element.orderType != CubiqTS.OrderType.LIMIT_CALL) {
        if (this.checkIfValid(element, data)) {
          if (element["positionBookNameCopy"]) {
            element["positionBookNameCopy"] = data.label;
          } else {
            element.positionBookName = data.label;
          }
          element.as_fv_positionBookName = data.value;
          element.positionBookId = data.value;

          updatedRows.push(element);
        }
      }
    });
    if (updatedRows.length > 0) {
      this.dataChangedSubject.next(updatedRows);
    }
  }

  checkIfValid(element: any, positionBook: any): boolean {
    if (element.isComponentLeg) {
      if (element.posBooks) {
        let validBook = false;
        element.posBooks.forEach((book) => {
          if (book.value == positionBook.value) {
            validBook = true;
          }
        });
        return validBook;
      }
      return false;
    }
    return element.traderGroupBranch == positionBook.branch || positionBook.isDefault;
  }

  setSlippage(value): void {
    this.slippage = value;
    let updatedRows: Array<any> = [];

    this.rowData.forEach((element) => {
      if (this.isSl(element)) {
        let pipSize = new CubiqTS.BigNumber("10").pow(-element.pipPrecision);
        element.slippage =
          element.initialSlippage != null
            ? element.initialSlippage.plus(value != null && value != "" ? value : 0)
            : value;
        let slippage = value != null && value != "" ? pipSize.times(value) : 0;
        let origMarketSpot = new CubiqTS.BigNumber(element.origSpotMarketRate);
        let newMarketSpot: CubiqTS.BigNumber;

        if (!element.clientBuysBase) {
          newMarketSpot = origMarketSpot.minus(slippage);
        } else {
          newMarketSpot = origMarketSpot.plus(slippage);
        }

        let minPrecision: number = Number(element.minPrecision);
        let currentDecs: number = newMarketSpot.decimalPlaces();
        if (currentDecs < minPrecision) {
          element.spotMarketRate = newMarketSpot.toFixed(minPrecision);
        } else {
          element.spotMarketRate = newMarketSpot.toFixed(currentDecs);
        }

        this.doClientSpot(element);

        updatedRows.push(element);

        if (element.leg1 != null && element.leg2 != null) {
          TriangulationUtil.triangulateFromOrder(element, element.leg1, element.leg2);
          updatedRows.push(element.leg1, element.leg2);
        }
      }
    });
    if (updatedRows.length > 0) {
      this.dataChangedSubject.next(updatedRows);
    }

    this.vwapUpdateSubject.next();
  }

  private doClientSpot(order): void {
    let clientDelta: CubiqTS.BigNumber = new CubiqTS.BigNumber(order.allInClientRate).minus(
      new CubiqTS.BigNumber(order.clientSpot),
    );
    let marketDelta: CubiqTS.BigNumber = new CubiqTS.BigNumber(order.allInClientRate).minus(
      new CubiqTS.BigNumber(order.forwardMarketRate),
    );

    let clientSpot: CubiqTS.BigNumber;
    let markup = order.markup == "-" || order.markup == null ? 0 : order.markup;
    if (!order.clientBuysBase)
      clientSpot = new CubiqTS.BigNumber(order.spotMarketRate).minus(markup);
    else clientSpot = new CubiqTS.BigNumber(order.spotMarketRate).plus(markup);

    let minPrecision: number = Number(order.minPrecision);
    let currentDecs: number = clientSpot.decimalPlaces();
    if (currentDecs < minPrecision) {
      order.clientSpot = clientSpot.toFixed(minPrecision);
    } else {
      order.clientSpot = clientSpot.toFixed(currentDecs);
    }

    order.allInClientRate = new CubiqTS.BigNumber(order.clientSpot).plus(clientDelta);
    order.forwardMarketRate = new CubiqTS.BigNumber(order.allInClientRate).minus(marketDelta);
  }

  public vehicleCcyChange(
    orderId: string,
    vehicleCcy: string,
    leg1Books?: any,
    leg2Books?: any,
  ): void {
    if (vehicleCcy) {
      this.removeCrossLegs(orderId);
      let origLeg = this.rowMap.get(orderId);
      // hide the position book, as will be using the component legs, keep a copy so we can restor if vehicle is removed
      origLeg["positionBookNameCopy"] = origLeg.positionBookName;
      origLeg.positionBookName = null;
      let pair1 = origLeg.crosses[0];
      let pair2 = origLeg.crosses[1];

      let leg1 = this.createLeg(
        1,
        orderId + LEG1,
        pair1.base,
        pair1.nonBase,
        pair1.minPrecision,
        leg1Books ? leg1Books.book.label : null,
        leg1Books ? leg1Books.book.value : null,
        leg1Books ? leg1Books.books : null,
        origLeg.deskId,
      );
      let leg2 = this.createLeg(
        2,
        orderId + LEG2,
        pair2.base,
        pair2.nonBase,
        pair2.minPrecision,
        leg2Books ? leg2Books.book.label : null,
        leg2Books ? leg2Books.book.value : null,
        leg2Books ? leg2Books.books : null,
        origLeg.deskId,
      );
      origLeg["leg1"] = leg1;
      origLeg["leg2"] = leg2;
      leg1["parentOrder"] = origLeg;
      leg1["leg2"] = leg2;
      leg2["parentOrder"] = origLeg;
      leg2["leg1"] = leg1;
      this.addRow(leg1);
      this.addRow(leg2);
    } else {
      this.removeCrossLegs(orderId);
      let origLeg = this.rowMap.get(orderId);
      origLeg.positionBookName = origLeg.positionBookNameCopy;
      origLeg["positionBookNameCopy"] = null;
    }
  }

  private removeCrossLegs(orderId: string) {
    this.deleteCrossLegsByOrderId(orderId);
    let origLeg = this.rowMap.get(orderId);
    origLeg["leg1"] = null;
    origLeg["leg2"] = null;
  }

  private createLeg(
    legNum: number,
    key: string,
    base: string,
    nonBase: string,
    minPrecision: number,
    bookName: string,
    bookId: string,
    books: Array<any>,
    deskId: string,
  ) {
    let row = {};
    row["legNum"] = legNum;
    row["instrument"] = base + "/" + nonBase;
    row["baseCcyCd"] = base;
    row["nonBaseCcyCd"] = nonBase;
    row["canOverRideStp"] = false;
    row["partFillEnabled"] = false;
    row["isComponentLeg"] = true;
    row["spotMarketRate"] = new CubiqTS.BigNumber(0).toFixed(minPrecision);
    row["minPrecision"] = minPrecision;
    row["key"] = key;
    row["positionBookName"] = bookName;
    row["as_fv_positionBookName"] = bookId;
    row["posBooks"] = books;
    row["deskId"] = deskId;
    row["legEnabled"] = true;
    return row;
  }

  /**
   * Get trader group branches of the currently selected orders in this group.
   * @returns the trader group branch if all selected orders in this group have the same
   * trader group branch, null if their is a mixture of orders with different trader group
   * branches
   */
  public getOrderTraderGroupBranch(): string {
    let branches: Set<string> = new Set(
      this.selectedIds
        .map((id) => this.rowMap.get(id))
        .filter((row) => row?.traderGroupBranch != null)
        .map((row) => row?.traderGroupBranch),
    );
    return branches.size == 1 ? branches.values().next().value : null;
  }

  public getRowFromId(orderId: string): any {
    return this.rowMap.get(orderId);
  }
}
